import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Avatar, Box } from '@mui/material';

const LazyImage = ({ 
  src, 
  alt, 
  width = 40, 
  height = 40, 
  borderRadius = 2,
  fallback = null,
  imageId = null, // Nowa prop dla pobierania obrazków przez API
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Asynchroniczne ładowanie obrazka
  const loadImageAsync = useCallback(async (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve(imageUrl);
      };
      img.onerror = (error) => {
        console.error('Failed to load image:', imageUrl, error);
        reject(new Error(`Failed to load image: ${imageUrl}`));
      };
      img.src = imageUrl;
    });
  }, []);

  useEffect(() => {
    // Intersection Observer for lazy loading
    if (imgRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsInView(true);
              observerRef.current?.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: '50px', // Start loading 50px before the image comes into view
        }
      );

      observerRef.current.observe(imgRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Asynchroniczne ładowanie obrazka gdy jest w widoku
  useEffect(() => {
    if (isInView && !imageSrc) {
      // Użyj requestIdleCallback dla lepszej wydajności
      const loadImage = async () => {
        try {
          let imageUrl = src;
          
          // Jeśli mamy imageId, pobierz obrazek przez API z autoryzacją
          if (imageId && (!src || src === 'null' || src === 'undefined')) {
            try {
              const { allegroApiService } = await import('../services/allegroApi');
              const blob = await allegroApiService.getProductImage(imageId);
              if (blob) {
                imageUrl = URL.createObjectURL(blob);
              } else {
                setHasError(true);
                return;
              }
            } catch (apiError) {
              console.error('Failed to load product image via API:', apiError);
              setHasError(true);
              return;
            }
          } else if (src) {
            // Sprawdź czy URL jest prawidłowy
            if (!src || src === 'null' || src === 'undefined') {
              setHasError(true);
              return;
            }
            imageUrl = src;
          } else {
            setHasError(true);
            return;
          }

          await loadImageAsync(imageUrl);
          setImageSrc(imageUrl);
          setIsLoaded(true);
        } catch (error) {
          console.error('Błąd podczas ładowania obrazka:', error);
          setHasError(true);
        }
      };

      // Sprawdź czy requestIdleCallback jest dostępny
      if (window.requestIdleCallback) {
        window.requestIdleCallback(() => loadImage());
      } else {
        // Fallback dla starszych przeglądarek
        setTimeout(() => loadImage(), 0);
      }
    }
  }, [isInView, src, imageSrc, loadImageAsync, imageId]);

  const handleError = () => {
    setHasError(true);
  };

  // If image is not in view, show placeholder
  if (!isInView) {
    return (
      <Avatar
        ref={imgRef}
        sx={{ 
          width, 
          height, 
          borderRadius,
          backgroundColor: 'grey.200',
        }}
        {...props}
      />
    );
  }

  // If there was an error, show fallback or placeholder
  if (hasError) {
    return fallback || (
      <Avatar
        sx={{ 
          width, 
          height, 
          borderRadius,
          backgroundColor: 'grey.200',
        }}
        {...props}
      />
    );
  }

  // Show loading placeholder while image is loading
  if (!imageSrc) {
    return (
      <Avatar
        ref={imgRef}
        sx={{ 
          width, 
          height, 
          borderRadius,
          backgroundColor: 'grey.200',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        {...props}
      >
        <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: 'grey.400' }} />
      </Avatar>
    );
  }

  return (
    <Avatar
      ref={imgRef}
      src={imageSrc}
      alt={alt}
      onError={handleError}
      sx={{ 
        width, 
        height, 
        borderRadius,
        opacity: isLoaded ? 1 : 0.7,
        transition: 'opacity 0.3s ease-in-out',
        backgroundColor: 'grey.200',
      }}
      {...props}
    />
  );
};

export default LazyImage; 